// Where this account is signed in, and closing the places that are not here.
//
// ======================================== WHY THIS ASKS FOR THE AUTHENTICATOR
//
// Every other read in this console costs a session token and nothing else. This
// one costs six digits as well, and the reason is not that sessions are more
// secret than reports — it is that this list is the one a thief would read.
//
// api/src/auth/routes.ts refused a session-reading route for years, in one
// sentence: "a GET /v1/auth/session answering this for a bearer token would
// answer somebody who found a token". The route that exists now answers a token
// AND a code from the phone, proved on this request, good for one time step and
// spendable once. So the person who stole a token cannot read where else you are
// signed in, and cannot close those places either.
//
// IT IS NOT ENOUGH TO SEND A SESSION THAT PROVED A FACTOR AT SIGN-IN. That is
// the mistake this file would be a vector for if it made it: a session carries
// the stamp of the factor it was opened with for its whole twelve hours, so a
// gate on that stamp would look exactly like a second-factor gate and would let
// a stolen token through all day. The server refuses the stamp and wants the
// code; this console asks for the code every time, and never caches one.
//
// ============================================ WHAT A 403 MEANS HERE, AND NOT
//
// enrol.ts is arranged around one 401 meaning two things. This file has the
// mirror problem on 403, and gets it right by the same method — read `error`,
// not the status:
//
//   403 second_factor_code_required  — no code was sent. Ask for one.
//   403 second_factor_code_wrong     — six digits, wrong or already spent. Retype.
//   403 second_factor_unavailable    — this account has no authenticator at all,
//                                      or this service holds no key. Neither is
//                                      retryable by typing, and the first is
//                                      fixed on the enrolment screen.
//
// None of them is a dead session, and none may be allowed to become one. The
// console maps a 401 to "signed out and forget the token"; if a mistyped digit
// took that path, somebody checking whether a stranger was in their account
// would be thrown back to their inbox for the trouble. freshFactor.ts chose 403
// over 401 on the server for exactly this reason and says so; this is the
// client half of that decision.

import { AUTH_ENDPOINTS, FRESH_FACTOR_FIELD } from "./endpoints.js";
import { authenticated, type Answer, type ApiOptions } from "./client.js";
import type { SessionToken } from "../session/store.js";

/**
 * One session on this account.
 *
 * NO ADDRESS AND NO DEVICE, and the absence is the service's rather than this
 * file's. 006 records `requested_ip` on `login_code` alone — "kept for security
 * audit and rate limiting, not for analytics", on rows deleted once expired —
 * and nothing has ever recorded a user agent, so there is no column to draw
 * from. The screen says so out loud rather than leaving a reader to assume a
 * blank column was withheld for this particular row.
 */
export type SignIn = {
  readonly sessionId: string;
  /** ISO 8601. When somebody signed in. */
  readonly issuedAt: string;
  /** ISO 8601, or null for a session that has not made a request since. */
  readonly lastSeenAt: string | null;
  readonly expiresAt: string;
  /** ISO 8601 when this was cut off, or null if it was not. */
  readonly revokedAt: string | null;
  readonly revokedBy: string | null;
  /** The one doing the asking. Never offered as something to close. */
  readonly current: boolean;
};

export type SignInList = {
  readonly sessions: readonly SignIn[];
  /** The service's own sentence about what it does and does not record. */
  readonly detail: string;
};

export type Ended = {
  readonly ended: number;
  readonly detail: string;
};

export type SignInRefusal =
  /** The session is not live. The caller clears it, exactly as a report 401 does. */
  | { readonly kind: "session_ended"; readonly detail: string }
  /** No code was sent, and one is wanted. The screen asks. */
  | { readonly kind: "code_required"; readonly detail: string }
  /** Six digits that did not verify, or were already spent. Retype. */
  | { readonly kind: "code_refused"; readonly detail: string }
  /**
   * There is no authenticator to check against — this account holds none, or
   * this service holds no key to open one with.
   *
   * Its own outcome because no amount of retyping fixes it and the way out is a
   * different screen. It is the honest answer rather than a gap: without a
   * second factor there is nothing that separates the person from somebody
   * holding their token, so there is no way to show this to one and not both.
   */
  | { readonly kind: "no_authenticator"; readonly detail: string }
  /** This service has no session routes at all. Not the person's fault. */
  | { readonly kind: "not_offered"; readonly detail: string }
  | { readonly kind: "unavailable"; readonly detail: string }
  | { readonly kind: "unreachable"; readonly detail: string };

export type SignInAttempt<T> =
  | { readonly ok: true; readonly value: T }
  | ({ readonly ok: false } & SignInRefusal);

/** Where this account is signed in. Costs a current authenticator code. */
export async function listSignIns(
  options: ApiOptions,
  token: SessionToken,
  code: string,
): Promise<SignInAttempt<SignInList>> {
  const answer = await authenticated(options, token, {
    path: AUTH_ENDPOINTS.listSessions,
    method: "POST",
    // The field name is the service's, imported rather than spelled. See
    // endpoints.ts: getting this wrong produces "you did not send a code" on a
    // request that did send one.
    json: { [FRESH_FACTOR_FIELD]: code },
  });

  if (!answer.ok) return { ok: false, ...signInRefusalFor(answer) };

  const list = asSignInList(answer.body);
  if (list === null) {
    // Refused rather than half-drawn, on beginEnrolment's reasoning. A partly
    // read list is worse here than elsewhere: the whole use of this screen is
    // noticing a row you do not recognise, and a list missing rows for a parsing
    // reason reads as an all-clear.
    return {
      ok: false,
      kind: "unreachable",
      detail:
        "The service answered with something that is not a list of sign-ins. Nothing is shown, because a " +
        "partly-read list would look like an all-clear.",
    };
  }

  return { ok: true, value: list };
}

/** End every other session on this account, keeping the one asking. */
export async function endOtherSignIns(
  options: ApiOptions,
  token: SessionToken,
  code: string,
): Promise<SignInAttempt<Ended>> {
  const answer = await authenticated(options, token, {
    path: AUTH_ENDPOINTS.endOtherSessions,
    method: "POST",
    json: { [FRESH_FACTOR_FIELD]: code },
  });

  if (!answer.ok) return { ok: false, ...signInRefusalFor(answer) };

  const body =
    typeof answer.body === "object" && answer.body !== null
      ? (answer.body as Record<string, unknown>)
      : null;
  const ended = typeof body?.["ended"] === "number" ? (body["ended"] as number) : null;
  if (ended === null) {
    // The dangerous half-read. "It worked" over an unknown number would tell
    // somebody the intruder is gone when nothing may have happened.
    return {
      ok: false,
      kind: "unreachable",
      detail:
        "The service accepted that code and did not say how many sessions it ended. Do not assume the " +
        "other sign-ins are closed: read the list again.",
    };
  }

  return {
    ok: true,
    value: {
      ended,
      detail:
        typeof body?.["detail"] === "string" && body["detail"] !== ""
          ? (body["detail"] as string)
          : `${ended} other sign-in${ended === 1 ? "" : "s"} ended.`,
    },
  };
}

/**
 * What a refusal on either route means.
 *
 * READS `error` AND NOT THE STATUS, for the reason in this file's header: three
 * unrelated things arrive as 403 and exactly one of them is worth retyping for.
 */
export function signInRefusalFor(answer: Extract<Answer, { ok: false }>): SignInRefusal {
  const error = typeof answer.body?.error === "string" ? answer.body.error : null;
  const said =
    typeof answer.body?.detail === "string" && answer.body.detail !== "" ? answer.body.detail : null;

  if (answer.status === null) {
    return {
      kind: "unreachable",
      detail: "The console could not reach the service. Nothing has been read and nothing has been ended.",
    };
  }

  // The only status that means the session itself is gone. `whoIsAsking` answers
  // 401 for an absent or dead token and nothing else does.
  if (answer.status === 401) {
    return { kind: "session_ended", detail: said ?? "That sign-in is no longer live. Sign in again." };
  }

  if (answer.status === 403) {
    if (error === "second_factor_code_required") {
      return {
        kind: "code_required",
        detail: said ?? "This needs a current code from your authenticator.",
      };
    }
    if (error === "second_factor_code_wrong") {
      // Verbatim where the server gave one. Its sentence covers a wrong code and
      // a spent one without saying which — login.ts's rule, so that somebody
      // replaying an observed code is not told they had the right digits at the
      // wrong moment — and it names both fixes.
      return {
        kind: "code_refused",
        detail: said ?? "That code did not verify. Wait for the next one and try again.",
      };
    }
    return {
      kind: "no_authenticator",
      detail:
        said ??
        "There is no authenticator on this account for the service to check, so it cannot tell you from " +
          "somebody holding your token. Add one, and this will work.",
    };
  }

  if (answer.status === 404) {
    // Fastify's own 404, carrying a `message` rather than our `detail`, so there
    // is usually nothing to quote. The routes are registered only where a
    // second-factor key is configured.
    return {
      kind: "not_offered",
      detail:
        "This service does not offer a list of sign-ins. That is how it is configured rather than anything " +
        "about this account: the list is only available where a second-factor key is set, because without " +
        "one there is no way to prove you are you rather than somebody holding your token.",
    };
  }

  return {
    kind: "unavailable",
    detail: said ?? "The service could not answer that just now. Nothing has been changed.",
  };
}

/** The list, or null if what came back was not one. */
function asSignInList(raw: unknown): SignInList | null {
  if (typeof raw !== "object" || raw === null) return null;
  const body = raw as Record<string, unknown>;
  const rows = body["sessions"];
  if (!Array.isArray(rows)) return null;

  const sessions: SignIn[] = [];
  for (const row of rows) {
    if (typeof row !== "object" || row === null) return null;
    const r = row as Record<string, unknown>;
    const sessionId = r["session_id"];
    const issuedAt = r["issued_at"];
    const expiresAt = r["expires_at"];
    // A row without these three is not a session, and dropping it quietly would
    // shorten the list — which on this screen is the failure that matters.
    if (typeof sessionId !== "string" || typeof issuedAt !== "string" || typeof expiresAt !== "string") {
      return null;
    }
    sessions.push({
      sessionId,
      issuedAt,
      expiresAt,
      lastSeenAt: typeof r["last_seen_at"] === "string" ? r["last_seen_at"] : null,
      revokedAt: typeof r["revoked_at"] === "string" ? r["revoked_at"] : null,
      revokedBy: typeof r["revoked_by"] === "string" ? r["revoked_by"] : null,
      current: r["current"] === true,
    });
  }

  return {
    sessions,
    detail: typeof body["detail"] === "string" ? body["detail"] : "",
  };
}
